import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { map, Subject, mergeMap, Subscription } from 'rxjs';
import { isVizSubType, VizSubtype } from '@lib.core/view/view-key';
import { LoggerService } from '@lib.core/logger/logger.service';
import { VizSubtypeStorageService } from '@public/src/app/store/store-access/viz-subtype-storage.service';

/** Chart subtypes only the portal's chart builder draws; the public pages read them as no subtype at all. */
const BUILDER_ONLY_SUBTYPES: readonly VizSubtype[] = ['heatmap', 'treemap'];

@Injectable({
  providedIn: 'root'
})
export class VizSubtypeQParamInteractor {
  private vizSubtypeStorageService: VizSubtypeStorageService = inject(VizSubtypeStorageService);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly vizSubtypeParam = 'vizSubtype';
  private readonly subscriptions: Subscription[] = [];
  private logger: LoggerService = LoggerService.getLoggerService();
  private readonly qParamReaderTrigger$$ = new Subject<void>();
  private readonly qParamReaderTrigger$ = this.qParamReaderTrigger$$.asObservable();

  constructor() {
    this.subscribeToQParamReader();
  }

  readVizSubtypeFromQParam() {
    this.qParamReaderTrigger$$.next();
  }

  destroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private subscribeToQParamReader() {
    this.subscriptions.push(
      this.qParamReaderTrigger$.pipe(
        mergeMap(() => this.activatedRoute.queryParams),
        map(qp => this.normalizeVizSubtype(qp[this.vizSubtypeParam]))
      )
      .subscribe({
        next: (vizSubtype: VizSubtype | null) => {
          this.vizSubtypeStorageService.setVizSubtype(vizSubtype);
        },
        error: (error) => this.logger.error(error),
      })
    );
  }

  private normalizeVizSubtype(vizSubtype: string | null | undefined): VizSubtype | null {
    if (!vizSubtype || !isVizSubType(vizSubtype) || BUILDER_ONLY_SUBTYPES.includes(vizSubtype)) {
      return null;
    }
    return vizSubtype as VizSubtype;
  }
}
